import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import {
  ROLES,
  createTester,
  listUsers,
  setUserDisabled,
  setUserLocale,
  setUserRole,
  type AdminUser,
} from "../api";
import { Avatar } from "../components/AccountMenu";
import { Amount } from "../components/Amount";
import Placeholder, { Pending } from "../components/Placeholder";
import { LOCALES, LOCALE_NAMES, useT, type Locale } from "../i18n";
import { sessionKey, useSession } from "../session";

/**
 * Admin is the platform admin's view of every account: who they are, their
 * role in their own library, the language their interface is in, and what they
 * spent this month.
 *
 * Roles and languages are set here and live in the database (ADR-0021,
 * ADR-0023). Who is a platform admin is not set here — that is ADMIN_EMAILS,
 * applied on sign-in.
 */
export default function Admin() {
  const { me, isPending } = useSession();
  const t = useT();
  const isAdmin = !!me?.user.platform_admin;
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: listUsers,
    enabled: isAdmin,
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8">
        <div className="flex items-baseline justify-between gap-4">
          <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
            {t.admin.title}
          </h1>
          <div className="flex gap-4">
            <Link
              to="/admin/collections"
              data-testid="admin-collections-link"
              className="text-sm font-medium text-sage-deep hover:underline"
            >
              {t.collections.link}
            </Link>
            <Link
              to="/admin/tags"
              data-testid="admin-tags-link"
              className="text-sm font-medium text-sage-deep hover:underline"
            >
              {t.tag.link}
            </Link>
            <Link
              to="/admin/activity"
              data-testid="admin-activity-link"
              className="text-sm font-medium text-sage-deep hover:underline"
            >
              {t.activity.link}
            </Link>
            <Link
              to="/admin/traffic"
              data-testid="admin-traffic-link"
              className="text-sm font-medium text-sage-deep hover:underline"
            >
              {t.traffic.link}
            </Link>
            <Link
              to="/admin/errors"
              data-testid="admin-errors-link"
              className="text-sm font-medium text-sage-deep hover:underline"
            >
              {t.errors.link}
            </Link>
          </div>
        </div>
        <p className="mt-1 text-sm text-muted">
          {users.data ? t.admin.accounts(users.data.items.length) : t.admin.accountsFallback}
        </p>
      </header>

      {(isPending || users.isPending) && (
        <Pending label={t.common.loading}>
          <Placeholder className="h-40 rounded-card" />
        </Pending>
      )}

      {users.isError && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t.admin.loadFailed}{" "}
          <button type="button" onClick={() => void users.refetch()} className="underline">
            {t.common.tryAgain}
          </button>
        </p>
      )}

      <NewTester />

      {users.data && (
        <ul
          data-testid="admin-users"
          className="divide-y divide-line rounded-card border border-line bg-surface"
        >
          {users.data.items.map((user) => (
            <UserRow key={user.id} user={user} self={user.id === me?.user.id} />
          ))}
        </ul>
      )}
    </main>
  );
}

function UserRow({ user, self }: { user: AdminUser; self: boolean }) {
  const client = useQueryClient();
  const t = useT();
  // Your own role decides what your header offers, and your own language what
  // the whole interface is in — so either change refreshes the session too.
  const changed = () => {
    void client.invalidateQueries({ queryKey: ["admin", "users"] });
    if (self) void client.invalidateQueries({ queryKey: sessionKey });
  };
  const role = useMutation({
    mutationFn: (next: string) => setUserRole(user.id, next),
    onSuccess: changed,
  });
  const locale = useMutation({
    mutationFn: (next: Locale) => setUserLocale(user.id, next),
    onSuccess: changed,
  });
  const switched = useMutation({
    mutationFn: (off: boolean) => setUserDisabled(user.id, off),
    onSuccess: changed,
  });
  const name = user.name || user.email;
  const select =
    "rounded-control border border-line bg-surface px-2 py-1.5 text-sm disabled:opacity-50";

  return (
    <li data-testid="admin-user" className="flex items-center gap-3 px-4 py-3">
      <Avatar name={name} imageURL={user.image_url} size={36} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium">
          {/* The name is the way into the account's usage: a link inside a
              truncated line of small print gets clipped off a phone screen. */}
          <Link
            to={`/admin/users/${user.id}`}
            data-testid="admin-user-usage"
            dir="auto"
            className="truncate hover:text-sage-deep hover:underline"
          >
            {name}
          </Link>
          {user.sign_in && user.sign_in !== "google" && (
            <span className="shrink-0 rounded-full border border-line px-2 py-0.5 text-[0.7rem] text-muted">
              {t.admin.wayIn[user.sign_in] ?? user.sign_in}
            </span>
          )}
          {user.disabled && (
            <span
              data-testid="admin-user-off"
              className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[0.7rem] font-medium text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              {t.admin.off}
            </span>
          )}
          {user.platform_admin && (
            <span className="shrink-0 rounded-full bg-sage-soft px-2 py-0.5 text-[0.7rem] font-medium text-sage-deep">
              {t.admin.badge}
            </span>
          )}
        </p>
        {user.name && <p className="truncate text-xs text-muted">{user.email}</p>}
        {/* The money gets its own line. Appended to the address it was the
            part that `truncate` ate on a phone, which is the half of the row
            this screen is for. */}
        <p className="text-xs text-muted">
          <Amount sentence={(a) => t.admin.spent(a)} amount={user.spent_usd} />
          {/* The ceiling that spend is checked against, so a figure on this
              row can be judged. Absent — not zero — for an account with no
              library, which has no ceiling to show (ADR-0082). */}
          {user.limit_usd !== undefined && (
            <span data-testid="admin-user-limit">
              {" · "}
              <Amount sentence={(a) => t.admin.limit(a)} amount={user.limit_usd} />
            </span>
          )}
        </p>
        {role.isError && (
          <p role="alert" className="text-xs text-red-600 dark:text-red-400">
            {role.error instanceof Error ? role.error.message : t.admin.roleFailed}
          </p>
        )}
        {switched.isError && (
          <p role="alert" className="text-xs text-red-600 dark:text-red-400">
            {switched.error instanceof Error ? switched.error.message : t.admin.switchFailed}
          </p>
        )}
        {locale.isError && (
          <p role="alert" className="text-xs text-red-600 dark:text-red-400">
            {locale.error instanceof Error ? locale.error.message : t.admin.localeFailed}
          </p>
        )}
      </div>
      {/* Stacked rather than side by side: two selects and a name do not fit
          one line of a phone. */}
      <div className="flex shrink-0 flex-col gap-1.5">
        <label className="sr-only" htmlFor={`role-${user.id}`}>
          {t.admin.roleFor(name)}
        </label>
        <select
          id={`role-${user.id}`}
          data-testid="admin-user-role"
          value={user.role}
          disabled={role.isPending || !user.role}
          onChange={(e) => role.mutate(e.target.value)}
          className={select}
        >
          {!user.role && <option value="">{t.admin.noLibrary}</option>}
          {ROLES.map((key) => (
            <option key={key} value={key}>
              {t.admin.roles[key]}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor={`locale-${user.id}`}>
          {t.admin.languageFor(name)}
        </label>
        <select
          id={`locale-${user.id}`}
          data-testid="admin-user-locale"
          value={user.locale}
          disabled={locale.isPending}
          onChange={(e) => locale.mutate(e.target.value as Locale)}
          className={select}
        >
          {LOCALES.map((key) => (
            <option key={key} value={key}>
              {LOCALE_NAMES[key]}
            </option>
          ))}
        </select>
        {/* Not offered for yourself or a platform admin: the server refuses
            both, since admins come from ADMIN_EMAILS (ADR-0145). */}
        {!self && !user.platform_admin && (
          <button
            type="button"
            data-testid="admin-user-switch"
            disabled={switched.isPending}
            onClick={() => switched.mutate(!user.disabled)}
            className="rounded-control border border-line px-2 py-1 text-xs disabled:opacity-50"
          >
            {user.disabled ? t.admin.switchOn : t.admin.switchOff}
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * NewTester makes a tester login (ADR-0145): a username in, a generated
 * password out, shown this once. For the Prolific testers and App Review.
 */
function NewTester() {
  const t = useT();
  const client = useQueryClient();
  const [username, setUsername] = useState("");
  const create = useMutation({
    mutationFn: (name: string) => createTester(name),
    onSuccess: () => {
      setUsername("");
      void client.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
  return (
    <section
      data-testid="admin-new-tester"
      className="mb-6 rounded-card border border-line bg-surface p-4"
    >
      <h2 className="text-sm font-semibold">{t.admin.testerTitle}</h2>
      <p className="mt-1 text-xs text-muted">{t.admin.testerWhat}</p>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (username.trim()) create.mutate(username.trim());
        }}
      >
        <label className="sr-only" htmlFor="tester-username">
          {t.admin.testerUsername}
        </label>
        <input
          id="tester-username"
          data-testid="admin-tester-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t.admin.testerUsername}
          autoCapitalize="none"
          autoCorrect="off"
          dir="ltr"
          className="min-w-0 flex-1 rounded-control border border-line bg-surface px-2 py-1.5 text-sm"
        />
        <button
          type="submit"
          data-testid="admin-tester-create"
          disabled={create.isPending || !username.trim()}
          className="rounded-control bg-sage-deep px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {t.admin.testerCreate}
        </button>
      </form>
      {create.isError && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
          {create.error instanceof Error ? create.error.message : t.admin.testerFailed}
        </p>
      )}
      {create.data && (
        <div data-testid="admin-tester-made" className="mt-3 text-xs">
          <p>{t.admin.testerMade(create.data.user.name || create.data.user.email)}</p>
          <p
            dir="ltr"
            className="mt-1 font-mono text-base select-all"
            data-testid="admin-tester-password"
          >
            {create.data.password}
          </p>
        </div>
      )}
    </section>
  );
}
